import React from 'react';
import { Table, Button, Collapse, Spinner } from 'react-bootstrap';

const ReportTable = ({
  items,
  searchTerm,
  openRows,
  onRowClick,
  checkedReportIndexes,
  onCheckboxChange,
  onUploadClick,
  onPreviewClick,
  onDownloadClick,
  onCommentClick,
  onCreateReport,  // New prop for handling Credit Letter creation
  sortConfig,
  onSort,
  previewLoadingMap = {}, // Per-item loading state for preview
}) => {
  // Filter items based on search term
  const filteredItems = items.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return [item.issuer, item.name, item.isin, item.paymentDate, item.status]
      .some(field => field && field.toLowerCase().includes(searchLower));
  });

  return (
    <div className="content-wrapper">
      <div className="content-scrollable">
        <Table bordered>
          <thead>
            <tr>
              <th>ISSUER</th>
              <th>SECURITY NAME</th>
              <th>ISIN</th>
              <th onClick={() => onSort('paymentDate')} style={{ cursor: 'pointer' }}>
                Payment Date{' '}
                {sortConfig.key === 'paymentDate'
                  ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                  : ''}
              </th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => {
              const isPreviewLoading = !!previewLoadingMap[item.id];
              return (
                <React.Fragment key={item.id}>
                  {/* Main row */}
                  <tr
                    onClick={() => onRowClick(item.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{item.issuer}</td>
                    <td>{item.name}</td>
                    <td>{item.isin}</td>
                    <td>{item.paymentDate}</td>
                    <td>{item.status}</td>
                  </tr>

                  {/* Collapsible details */}
                  <tr>
                    <Collapse in={openRows.includes(item.id)}>
                      <td colSpan={5}>
                        <Table bordered className="collapse-table">
                          <tbody>
                            {item.reports.length > 0 ? (
                              item.reports.map((report, idx) => {
                                const key = `${item.id}-${idx}`;
                                // Credit Letter with empty availability: show Create Report
                                if (
                                  report.title === "Credit Letter" &&
                                  report.availability === ""
                                ) {
                                  return (
                                    <tr key={idx}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={checkedReportIndexes.has(key)}
                                          onChange={(e) =>
                                            onCheckboxChange(item.id, idx, e.target.checked)
                                          }
                                          disabled={false}
                                        />
                                      </td>
                                      <td>{report.title}</td>
                                      <td>
                                        {report.availability}
                                        {report.comment && (
                                          <span
                                            style={{ cursor: 'pointer', marginLeft: '5px' }}
                                            onClick={() => onCommentClick(report.comment)}
                                          >
                                            &#9865;
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <Button
                                          variant="primary"
                                          className="action-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onCreateReport(report, item);
                                          }}
                                        >
                                          Create Report
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                }

                                // Entitlement Report Excel Version
                                if (report.title === 'Entitlement Report Excel Version') {
                                  const availability = report.availability;
                                  const disableCheckbox = !['Report Available', 'Not Available', 'For Adjustment'].includes(availability);
                                  return (
                                    <tr key={idx}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={checkedReportIndexes.has(key)}
                                          onChange={(e) =>
                                            onCheckboxChange(item.id, idx, e.target.checked)
                                          }
                                          disabled={disableCheckbox}
                                        />
                                      </td>
                                      <td>{report.title}</td>
                                      <td>
                                        {availability}
                                        {report.comment && (
                                          <span
                                            style={{ cursor: 'pointer', marginLeft: '5px' }}
                                            onClick={() => onCommentClick(report.comment)}
                                          >
                                            &#9865;
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        {(availability === 'Report Available' || availability === 'For Adjustment') ? (
                                          <>
                                            <Button
                                              variant="primary"
                                              className="action-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onUploadClick(report, item);
                                              }}
                                            >
                                              Upload
                                            </Button>
                                            <Button
                                              variant="primary"
                                              className="action-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onPreviewClick(report, item);
                                              }}
                                              disabled={isPreviewLoading}
                                            >
                                              {isPreviewLoading ? (
                                                <>
                                                  <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                  /> Previewing...
                                                </>
                                              ) : (
                                                'Preview'
                                              )}
                                            </Button>
                                            <Button
                                              variant="primary"
                                              className="action-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadClick(report, item);
                                              }}
                                            >
                                              Download
                                            </Button>
                                          </>
                                        ) : availability === 'Not Available' ? (
                                          <Button
                                            variant="primary"
                                            className="action-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onUploadClick(report, item);
                                            }}
                                          >
                                            Upload
                                          </Button>
                                        ) : (
                                          <>
                                            <Button
                                              variant="primary"
                                              className="action-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onPreviewClick(report, item);
                                              }}
                                              disabled={isPreviewLoading}
                                            >
                                              {isPreviewLoading ? (
                                                <>
                                                  <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                  /> Previewing...
                                                </>
                                              ) : (
                                                'Preview'
                                              )}
                                            </Button>
                                            <Button
                                              variant="primary"
                                              className="action-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadClick(report, item);
                                              }}
                                            >
                                              Download
                                            </Button>
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }

                                // Other report types
                                const availability = report.availability;
                                const disableCheckbox = !['Report Available', 'Not Available', 'For Adjustment'].includes(availability);
                                return (
                                  <tr key={idx}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={checkedReportIndexes.has(key)}
                                        onChange={(e) =>
                                          onCheckboxChange(item.id, idx, e.target.checked)
                                        }
                                        disabled={disableCheckbox}
                                      />
                                    </td>
                                    <td>{report.title}</td>
                                    <td>
                                      {availability}
                                      {report.comment && (
                                        <span
                                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                                          onClick={() => onCommentClick(report.comment)}
                                        >
                                          &#9865;
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      {(availability === 'Report Available' || availability === 'For Adjustment') ? (
                                        <>
                                          <Button
                                            variant="primary"
                                            className="action-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onUploadClick(report, item);
                                            }}
                                          >
                                            Upload
                                          </Button>
                                          <Button
                                            variant="primary"
                                            className="action-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onPreviewClick(report, item);
                                            }}
                                            disabled={isPreviewLoading}
                                          >
                                            {isPreviewLoading ? (
                                              <>
                                                <Spinner
                                                  as="span"
                                                  animation="border"
                                                  size="sm"
                                                  role="status"
                                                  aria-hidden="true"
                                                /> Previewing...
                                              </>
                                            ) : (
                                              'Preview'
                                            )}
                                          </Button>
                                          <Button
                                            variant="primary"
                                            className="action-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDownloadClick(report, item);
                                            }}
                                          >
                                            Download
                                          </Button>
                                        </>
                                      ) : availability === 'Not Available' ? (
                                        <Button
                                          variant="primary"
                                          className="action-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onUploadClick(report, item);
                                          }}
                                          disabled
                                        >
                                          Upload
                                        </Button>
                                      ) : (
                                        <>
                                          <Button
                                            variant="primary"
                                            className="action-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onPreviewClick(report, item);
                                            }}
                                            disabled={isPreviewLoading}
                                          >
                                            {isPreviewLoading ? (
                                              <>
                                                <Spinner
                                                  as="span"
                                                  animation="border"
                                                  size="sm"
                                                  role="status"
                                                  aria-hidden="true"
                                                /> Previewing...
                                              </>
                                            ) : (
                                              'Preview'
                                            )}
                                          </Button>
                                          <Button
                                            variant="primary"
                                            className="action-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDownloadClick(report, item);
                                            }}
                                          >
                                            Download
                                          </Button>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={4} style={{ textAlign: 'center' }}>No reports</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </td>
                    </Collapse>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ReportTable;
